import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  DragIndicatorRounded,
  SaveRounded,
  CloseRounded,
  ViewListRounded,
  ArrowUpwardRounded,
  ArrowDownwardRounded,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  collection,
  onSnapshot,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { callable } from '../../lib/callable';
import { useTenantStore } from '../../stores/tenant.store';
import { errorText } from '../../lib/error-text';
import { formatDate } from '../../lib/i18n-format';

// ─── Types ──────────────────────────────────────────────────────────────────

export type FieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'date'
  | 'select'
  | 'checkbox'
  | 'file';

export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  required: boolean;
  options?: string[]; // for select
  helpText?: string;
}

export interface FormTemplate {
  id: string;
  name: string;
  description?: string;
  fields: FormField[];
  createdAt?: Date;
}

// Stable, ordered list of field-type ids. Labels are resolved at render time
// via t('formTemplates.fieldTypes.<id>'); the ids below are the values persisted
// in Firestore and must not change.
const FIELD_TYPE_IDS: FieldType[] = [
  'text',
  'textarea',
  'email',
  'phone',
  'date',
  'select',
  'checkbox',
  'file',
];

// ─── Field Editor Row ─────────────────────────────────────────────────────────

interface FieldRowProps {
  field: FormField;
  index: number;
  total: number;
  onChange: (updated: FormField) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function FieldRow({
  field,
  index,
  total,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}: FieldRowProps) {
  const { t } = useTranslation('sis');
  const [expanded, setExpanded] = useState(field.label === '');
  const [optionsText, setOptionsText] = useState(field.options?.join('\n') ?? '');

  function update(patch: Partial<FormField>) {
    onChange({ ...field, ...patch });
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid rgba(255,255,255,0.07)',
        background: expanded ? 'rgba(99,102,241,0.04)' : 'rgba(255,255,255,0.02)',
        transition: 'background 0.2s',
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <DragIndicatorRounded sx={{ color: 'text.disabled', flexShrink: 0, cursor: 'grab' }} />
        <Chip
          label={t(`formTemplates.fieldTypes.${field.type}`)}
          size="small"
          variant="outlined"
          sx={{ fontSize: 11, flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ flex: 1, cursor: 'pointer', color: field.label ? 'text.primary' : 'text.disabled' }}
          onClick={() => setExpanded((p) => !p)}
        >
          {field.label || t('formTemplates.field.noLabel')}
        </Typography>
        {field.required && (
          <Chip
            label={t('formTemplates.field.requiredChip')}
            size="small"
            color="primary"
            sx={{ fontSize: 10 }}
          />
        )}
        <Stack direction="row" gap={0}>
          <Tooltip title={t('formTemplates.field.moveUp')}>
            <span>
              <IconButton size="small" onClick={onMoveUp} disabled={index === 0}>
                <ArrowUpwardRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('formTemplates.field.moveDown')}>
            <span>
              <IconButton size="small" onClick={onMoveDown} disabled={index === total - 1}>
                <ArrowDownwardRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={expanded ? t('formTemplates.field.close') : t('formTemplates.field.edit')}
          >
            <IconButton size="small" onClick={() => setExpanded((p) => !p)}>
              {expanded ? <CloseRounded fontSize="small" /> : <EditRounded fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('formTemplates.field.deleteTooltip')}>
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {expanded && (
        <Stack gap={2} mt={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              label={t('formTemplates.field.label')}
              size="small"
              fullWidth
              value={field.label}
              onChange={(e) => update({ label: e.target.value })}
              placeholder={t('formTemplates.field.labelPlaceholder')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }}>
              <InputLabel>{t('formTemplates.field.type')}</InputLabel>
              <Select
                label={t('formTemplates.field.type')}
                value={field.type}
                onChange={(e) => update({ type: e.target.value as FieldType })}
                sx={{ borderRadius: 2 }}
              >
                {FIELD_TYPE_IDS.map((val) => (
                  <MenuItem key={val} value={val}>
                    {t(`formTemplates.fieldTypes.${val}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="flex-start">
            <TextField
              label={t('formTemplates.field.helpText')}
              size="small"
              fullWidth
              value={field.helpText ?? ''}
              onChange={(e) => update({ helpText: e.target.value })}
              placeholder={t('formTemplates.field.helpTextPlaceholder')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.required}
                  onChange={(e) => update({ required: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('formTemplates.field.required')}</Typography>}
              sx={{ flexShrink: 0, mt: 0.5 }}
            />
          </Stack>

          {field.type === 'select' && (
            <TextField
              label={t('formTemplates.field.options')}
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={optionsText}
              onChange={(e) => {
                setOptionsText(e.target.value);
                update({
                  options: e.target.value
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                });
              }}
              placeholder={t('formTemplates.field.optionsPlaceholder')}
              helperText={t('formTemplates.field.optionsHelper')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}
        </Stack>
      )}
    </Paper>
  );
}

// ─── Template Editor Dialog ──────────────────────────────────────────────────

interface TemplateEditorProps {
  open: boolean;
  initial: FormTemplate | undefined;
  onClose: () => void;
  onSave: (data: { name: string; description: string; fields: FormField[] }) => Promise<void>;
  saving: boolean;
  saveError: string | null;
}

function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function TemplateEditor({
  open,
  initial,
  onClose,
  onSave,
  saving,
  saveError,
}: TemplateEditorProps) {
  const { t } = useTranslation('sis');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [fields, setFields] = useState<FormField[]>(initial?.fields ?? []);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setDescription(initial?.description ?? '');
      setFields(initial?.fields ?? []);
    }
  }, [open, initial]);

  function addField(type: FieldType) {
    setFields((prev) => [
      ...prev,
      {
        id: generateId(),
        type,
        label: '',
        required: false,
      },
    ]);
  }

  function updateField(index: number, updated: FormField) {
    setFields((prev) => prev.map((f, i) => (i === index ? updated : f)));
  }

  function deleteField(index: number) {
    setFields((prev) => prev.filter((_, i) => i !== index));
  }

  function moveField(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= fields.length) return;
    setFields((prev) => {
      const arr = [...prev];
      [arr[index], arr[next]] = [arr[next]!, arr[index]!];
      return arr;
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, border: '1px solid rgba(255,255,255,0.08)', maxHeight: '90vh' },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ViewListRounded sx={{ color: 'primary.main' }} />
        {initial ? t('formTemplates.editor.editTitle') : t('formTemplates.editor.newTitle')}
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 3 }}>
        {saveError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {saveError}
          </Alert>
        )}

        <Stack gap={2}>
          <TextField
            label={t('formTemplates.editor.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            placeholder={t('formTemplates.editor.namePlaceholder')}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            label={t('formTemplates.editor.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            placeholder={t('formTemplates.editor.descriptionPlaceholder')}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Stack>

        <Divider />

        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle2" fontWeight={700}>
              {t('formTemplates.editor.fieldsHeading', { count: fields.length })}
            </Typography>
          </Stack>

          <Stack gap={1.5} mb={2}>
            {fields.map((field, index) => (
              <FieldRow
                key={field.id}
                field={field}
                index={index}
                total={fields.length}
                onChange={(updated) => updateField(index, updated)}
                onDelete={() => deleteField(index)}
                onMoveUp={() => moveField(index, -1)}
                onMoveDown={() => moveField(index, 1)}
              />
            ))}
            {fields.length === 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  textAlign: 'center',
                  border: '1px dashed rgba(255,255,255,0.15)',
                  background: 'transparent',
                }}
              >
                <Typography variant="body2" color="text.disabled">
                  {t('formTemplates.editor.noFields')}
                </Typography>
              </Paper>
            )}
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {t('formTemplates.editor.addField')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {FIELD_TYPE_IDS.map((type) => (
                <Button
                  key={type}
                  size="small"
                  variant="outlined"
                  onClick={() => addField(type)}
                  startIcon={<AddRounded />}
                  sx={{ borderRadius: 2, fontSize: 12 }}
                >
                  {t(`formTemplates.fieldTypes.${type}`)}
                </Button>
              ))}
            </Stack>
          </Box>

          <FormHelperText sx={{ mt: 1 }}>
            {t('formTemplates.editor.atLeastOneRecommended')}
          </FormHelperText>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>
          {t('formTemplates.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRounded />}
          onClick={() => onSave({ name, description, fields })}
          disabled={saving || !name.trim()}
          sx={{ borderRadius: 2, px: 3 }}
        >
          {saving ? t('formTemplates.actions.saving') : t('formTemplates.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function FormTemplatesPage() {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<FormTemplate | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FormTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Firestore live collection
  useEffect(() => {
    if (!currentTenantId) return;
    const ref = collection(db, `tenants/${currentTenantId}/formTemplates`);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const docs = snap.docs.map((d: QueryDocumentSnapshot<DocumentData>) => {
          const data = d.data();
          return {
            id: d.id,
            name: data['name'] ?? '',
            description: data['description'],
            fields: (data['fields'] as FormField[]) ?? [],
            createdAt: data['createdAt']?.toDate?.(),
          } as FormTemplate;
        });
        setTemplates(docs);
        setLoadingList(false);
      },
      () => setLoadingList(false),
    );
    return () => unsub();
  }, [currentTenantId]);

  const handleSave = async (data: { name: string; description: string; fields: FormField[] }) => {
    if (!currentTenantId) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (editTarget) {
        const fn = callable('updateFormTemplate');
        await fn({ tenantId: currentTenantId, templateId: editTarget.id, ...data });
      } else {
        const fn = callable('createFormTemplate');
        await fn({ tenantId: currentTenantId, ...data });
      }
      setEditorOpen(false);
      setEditTarget(undefined);
    } catch (err) {
      setSaveError(errorText(err, t('formTemplates.errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentTenantId || !deleteTarget) return;
    setDeleting(true);
    try {
      const fn = callable('deleteFormTemplate');
      await fn({ tenantId: currentTenantId, templateId: deleteTarget.id });
      setDeleteTarget(null);
    } catch (err) {
      console.error('[FormTemplates] delete error', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={4}
        gap={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} color="white">
            {t('formTemplates.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('formTemplates.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          onClick={() => {
            setEditTarget(undefined);
            setEditorOpen(true);
          }}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
          }}
        >
          {t('formTemplates.actions.new')}
        </Button>
      </Stack>

      {/* List */}
      {loadingList ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress sx={{ color: '#6366f1' }} />
        </Box>
      ) : templates.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed rgba(255,255,255,0.12)',
            background: 'transparent',
          }}
        >
          <ViewListRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
            {t('formTemplates.list.emptyTitle')}
          </Typography>
          <Typography variant="body2" color="text.disabled" mb={3}>
            {t('formTemplates.list.emptyBody')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddRounded />}
            onClick={() => {
              setEditTarget(undefined);
              setEditorOpen(true);
            }}
            sx={{ borderRadius: 2 }}
          >
            {t('formTemplates.actions.create')}
          </Button>
        </Paper>
      ) : (
        <Stack gap={2}>
          {templates.map((tpl) => (
            <Paper
              key={tpl.id}
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: '1px solid rgba(99,102,241,0.15)',
                background: 'rgba(255,255,255,0.02)',
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: 'rgba(99,102,241,0.35)' },
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                gap={1.5}
              >
                <Box flex={1}>
                  <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {tpl.name}
                    </Typography>
                    <Chip
                      label={t('formTemplates.fieldsCount', { count: tpl.fields.length })}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  </Stack>
                  {tpl.description && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {tpl.description}
                    </Typography>
                  )}
                  {tpl.createdAt && (
                    <Typography variant="caption" color="text.disabled">
                      {t('formTemplates.createdOn', {
                        date: formatDate(tpl.createdAt),
                      })}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" gap={1}>
                  <Tooltip title={t('formTemplates.list.editTooltip')}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditTarget(tpl);
                        setEditorOpen(true);
                      }}
                      sx={{ color: 'primary.main' }}
                    >
                      <EditRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('formTemplates.list.deleteTooltip')}>
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(tpl)}>
                      <DeleteRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              {/* Field preview chips */}
              {tpl.fields.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1.5}>
                  {tpl.fields.slice(0, 6).map((f) => (
                    <Chip
                      key={f.id}
                      label={f.label || t(`formTemplates.fieldTypes.${f.type}`)}
                      size="small"
                      sx={{
                        fontSize: 11,
                        bgcolor: 'rgba(99,102,241,0.08)',
                        border: '1px solid rgba(99,102,241,0.15)',
                      }}
                    />
                  ))}
                  {tpl.fields.length > 6 && (
                    <Chip
                      label={t('formTemplates.moreFields', { count: tpl.fields.length - 6 })}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  )}
                </Stack>
              )}
            </Paper>
          ))}
        </Stack>
      )}

      {/* Editor Dialog */}
      <TemplateEditor
        open={editorOpen}
        initial={editTarget}
        onClose={() => {
          setEditorOpen(false);
          setEditTarget(undefined);
          setSaveError(null);
        }}
        onSave={handleSave}
        saving={saving}
        saveError={saveError}
      />

      {/* Delete Confirm Dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: 3, border: '1px solid rgba(255,255,255,0.08)' } }}
      >
        <DialogTitle fontWeight={700}>{t('formTemplates.delete.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            <Trans
              i18nKey="formTemplates.delete.body"
              t={t}
              values={{ name: deleteTarget?.name ?? '' }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('formTemplates.actions.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ borderRadius: 2 }}
          >
            {deleting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t('formTemplates.actions.delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
